import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Lock, RefreshCw, Mail, Phone, Globe, Instagram, Facebook, ChevronDown, ChevronUp, LogOut } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "haus_admin_token";

const PKG_COLORS = {
  starter: "#00B2A9",
  all_star: "#EF426F",
  championship: "#FF8200",
};

const fmtMoney = (n, ccy = "usd") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: (ccy || "usd").toUpperCase() }).format(n || 0);

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return s;
  }
};

const StatusPill = ({ status }) => {
  const map = {
    paid: { bg: "#00B2A9", label: "Paid" },
    unpaid: { bg: "#71717a", label: "Pending" },
    pending: { bg: "#71717a", label: "Pending" },
    no_payment_required: { bg: "#71717a", label: "—" },
  };
  const s = map[status] || { bg: "#EF426F", label: status || "—" };
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.15em] text-white"
      style={{ background: s.bg }}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-white/80" />
      {s.label}
    </span>
  );
};

const AdminPage = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [tokenInput, setTokenInput] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [authErr, setAuthErr] = useState("");
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async (t) => {
    if (!t) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/orders`, { headers: { "X-Admin-Token": t } });
      setData(res.data);
      setAuthErr("");
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setAuthErr("Invalid admin token.");
      } else {
        toast.error("Could not load orders.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    localStorage.setItem(TOKEN_KEY, tokenInput.trim());
    setToken(tokenInput.trim());
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData(null);
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <Toaster position="top-center" richColors />
        <form
          onSubmit={handleLogin}
          className="max-w-sm w-full bg-white rounded-3xl p-8 border border-black/5 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15)]"
        >
          <div className="w-12 h-12 rounded-2xl bg-[#0A0A0A] flex items-center justify-center mb-5">
            <Lock size={20} className="text-white" />
          </div>
          <h1 className="text-2xl font-black tracking-tight">Admin Access</h1>
          <p className="mt-1.5 text-sm text-zinc-500">Enter your admin token to view orders.</p>
          <input
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            placeholder="Admin token"
            data-testid="admin-token-input"
            className="mt-6 w-full rounded-xl border border-black/10 px-4 py-3 text-sm font-medium bg-white focus:outline-none focus:ring-2 focus:ring-[#00B2A9]"
          />
          {authErr && <p className="mt-2 text-sm font-semibold text-[#EF426F]">{authErr}</p>}
          <button
            type="submit"
            data-testid="admin-login-submit"
            className="mt-5 w-full rounded-full bg-[#0A0A0A] text-white font-bold py-3.5 text-sm tracking-wider uppercase"
          >
            Sign In
          </button>
          <a href="/" className="block mt-4 text-center text-xs uppercase tracking-[0.2em] font-bold text-zinc-500 hover:text-[#0A0A0A]">
            ← Back to site
          </a>
        </form>
      </div>
    );
  }

  const stats = data?.stats || { total_orders: 0, paid_orders: 0, revenue: 0 };
  const orders = data?.orders || [];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Toaster position="top-center" richColors />

      <header className="bg-white border-b border-black/5 sticky top-0 z-10 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xl">🏀</span>
            <div>
              <div className="font-black tracking-tight">Haus of Chi · Admin</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Spurs Finals Orders</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => load(token)}
              disabled={loading}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-black/10 text-xs font-bold uppercase tracking-wider hover:bg-zinc-50 disabled:opacity-50"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
            <button
              type="button"
              onClick={handleLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-black/10 text-xs font-bold uppercase tracking-wider hover:bg-zinc-50"
            >
              <LogOut size={12} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            { label: "Total orders", value: stats.total_orders, accent: "#0A0A0A" },
            { label: "Paid", value: stats.paid_orders, accent: "#00B2A9" },
            { label: "Revenue", value: fmtMoney(stats.revenue), accent: "#FF8200" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-3xl border border-black/5 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 font-bold">{s.label}</div>
              <div className="mt-2 text-3xl md:text-4xl font-black tracking-tight" style={{ color: s.accent }}>
                {s.value}
              </div>
            </div>
          ))}
        </div>

        {/* Orders list */}
        <div className="mt-8">
          <h2 className="text-lg font-black tracking-tight uppercase tracking-[0.15em] mb-4">All orders</h2>

          {orders.length === 0 ? (
            <div className="bg-white border border-dashed border-black/15 rounded-3xl p-12 text-center text-zinc-500">
              No orders yet. Once a customer pays, they'll show up here.
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => {
                const isOpen = !!expanded[o.session_id];
                return (
                  <div
                    key={o.session_id}
                    data-testid={`admin-order-${o.session_id}`}
                    className="bg-white rounded-2xl border border-black/5 shadow-[0_4px_20px_rgb(0,0,0,0.03)] overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => setExpanded({ ...expanded, [o.session_id]: !isOpen })}
                      className="w-full p-5 md:p-6 text-left flex items-center gap-4 hover:bg-zinc-50/50 transition"
                    >
                      <div className="w-1 self-stretch rounded-full" style={{ background: PKG_COLORS[o.package_id] || "#0A0A0A" }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-black tracking-tight truncate">{o.name || "—"}</span>
                          <span className="text-zinc-400">·</span>
                          <span className="text-sm font-semibold text-zinc-600 truncate">{o.business || "—"}</span>
                        </div>
                        <div className="mt-1 text-xs text-zinc-500">
                          {o.package_name} · {fmtDate(o.created_at)}
                        </div>
                      </div>
                      <div className="hidden md:flex flex-col items-end gap-1.5 shrink-0">
                        <div className="text-xl font-black tracking-tight">{fmtMoney(o.amount, o.currency)}</div>
                        <StatusPill status={o.payment_status} />
                      </div>
                      <div className="md:hidden">
                        <StatusPill status={o.payment_status} />
                      </div>
                      {isOpen ? <ChevronUp size={18} className="text-zinc-400" /> : <ChevronDown size={18} className="text-zinc-400" />}
                    </button>

                    {isOpen && (
                      <div className="px-5 md:px-6 pb-6 border-t border-black/5 pt-5 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 mb-3">Customer</div>
                          <Row icon={<Mail size={14} />} label="Email" value={o.email} link={`mailto:${o.email}`} />
                          <Row icon={<Phone size={14} />} label="Phone" value={o.intake?.phone} link={o.intake?.phone ? `tel:${o.intake.phone.replace(/\D/g,'')}` : null} />
                          <Row icon={<Globe size={14} />} label="Website" value={o.intake?.website} link={o.intake?.website} />
                          <Row icon={<Instagram size={14} />} label="Instagram" value={o.intake?.instagram} />
                          <Row icon={<Facebook size={14} />} label="Facebook" value={o.intake?.facebook} />
                          <div className="mt-3 text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 mb-1">Stripe session</div>
                          <div className="font-mono text-[11px] text-zinc-600 break-all">{o.session_id}</div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500 mb-3">Intake</div>
                          {o.intake ? (
                            <div className="space-y-3">
                              <Block label="Target customer" value={o.intake.target_customer} />
                              <Block label="Promo ideas" value={o.intake.promo_ideas} />
                              <Block label="Notes" value={o.intake.notes} />
                            </div>
                          ) : (
                            <p className="text-sm text-zinc-500 italic">
                              No intake submitted yet. Customer may have chosen "Text Chi" instead.
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const Row = ({ icon, label, value, link }) => {
  if (!value) {
    return (
      <div className="flex items-center gap-2 py-1.5 text-sm">
        <span className="text-zinc-400">{icon}</span>
        <span className="text-zinc-400 italic">— {label} not provided</span>
      </div>
    );
  }
  const content = (
    <span className="font-semibold text-[#0A0A0A] break-all">{value}</span>
  );
  return (
    <div className="flex items-center gap-2 py-1.5 text-sm">
      <span className="text-zinc-500">{icon}</span>
      {link ? (
        <a href={link} target="_blank" rel="noreferrer" className="hover:underline">
          {content}
        </a>
      ) : (
        content
      )}
    </div>
  );
};

const Block = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 mb-1">{label}</div>
    <p className="text-sm text-zinc-700 leading-relaxed whitespace-pre-wrap">
      {value || <span className="italic text-zinc-400">Not provided</span>}
    </p>
  </div>
);

export default AdminPage;
